import { FormProvider, useFormContext } from "react-hook-form";

import Box from "@/components/Box";
import CustomerPage from "@/components/CustomerPage";
import { ExternalTextLink, InternalTextLink } from "@/components/Links";
import { theme } from "@/core/theme";
import { CMP_SIGNIN_URL } from "@/core/urls";
import { REGENERUS_PRIVACY_URL, REGENERUS_TERMS_OF_USE_URL } from "@/core/urls";
import { SPECIALISMS_AND_PRACTITIONER_TYPES_QUERY } from "@/graphql/accounts";
import useScrollToError from "@/hooks/form/useScrollToError";
import Asterisk from "@/tpo/Asterisk";
import Checkbox from "@/tpo/Checkbox";
import ChevronComponent from "@/tpo/Chevron";
import ControlledFormField from "@/tpo/ControlledFormField";
import ControlledRadioGroup from "@/tpo/ControlledRadioGroup";
import DateInput from "@/tpo/DateInput";
import FloatingLabelInput from "@/tpo/FloatingLabelInput";
import FormControl from "@/tpo/FormControl";
import { Error } from "@/tpo/InputWrapper";
import FloatingLabelPasswordInput from "@/tpo/PasswordInput";
import ShippingCountrySelect from "@/tpo/ShippingCountrySelect";
import Spacer from "@/tpo/Spacer";
import Stack from "@/tpo/Stack";
import BasePractitionerTypeField from "@/tpo/partnerDashboard/practitioner/Application/ui/PractitionerTypeField";
import ButtonV2 from "@/v2/Buttons";
import { useQuery } from "@apollo/client";
import { createGlobalStyle } from "styled-components";

import ProfessionalBodiesField from "./ProfessionalBodiesField";
import QualificationProof from "./QualificationProof";
import RequiredFieldsNotice from "./RequiredFieldsNotice";
import SpecialismsField from "./SpecialismsField";

const GlobalStyle = createGlobalStyle`
  .PoweredByRegenerusBanner svg {
    fill: black;
  }
`;

function PractitionerTypeField({ practitionerTypes }) {
  const { setValue, clearErrors } = useFormContext();

  return (
    <BasePractitionerTypeField
      practitionerTypes={practitionerTypes}
      onChange={val => {
        setValue("practitionerType", val);
        setValue("documents", []);
        setValue("professionalBodies", []);
        clearErrors(["practitionerType", "documents", "professionalBodies"]);
      }}
    />
  );
}

export default function Common({
  api,
  showLoginLink = false,
  showPasswordFields = false,
  readOnlyFields = []
}) {
  const { data: { practitionerTypes = [], specialisms = [] } = {} } = useQuery(
    SPECIALISMS_AND_PRACTITIONER_TYPES_QUERY
  );

  useScrollToError(api);

  return (
    <FormProvider {...api.methods}>
      <CustomerPage
        bg="haze"
        jumbotronProps={{
          bg: "partners",
          title: "Practitioner application",
          color: "dark"
        }}
      >
        <GlobalStyle />
        <Box pt={theme.spacing.section.pt} pb={theme.spacing.section.pb} px={[20, 20, "5.5vw"]}>
          <Box maxWidth={760} mx="auto">
            <Box fontFamily="gilroyBold" fontSize={[28, 28, 36]}>
              Register for a Practitioner Account
            </Box>
            <Spacer py={2} />
            <Box
              as="ul"
              fontFamily="gilroyMedium"
              fontSize={16}
              listStyle="disc"
              style={{
                listStylePosition: "inside"
              }}
            >
              The Practitioner level of access is for qualified health professionals.
              <Spacer py={2} />
              <Box as="li" fontFamily="inherit">
                Full access to our exclusive suite of 200+ tests at trade prices.
              </Box>
              <Box as="li" fontFamily="inherit">
                Order & manage tests on behalf of your patients.
              </Box>
              <Box as="li" fontFamily="inherit">
                Review and release test results via the patient platform
              </Box>
            </Box>
            <Spacer py={20} />
            <Stack
              as="form"
              gap={[20, 20, 40]}
              onSubmit={e => {
                api.methods.clearErrors();
                api.methods.handleSubmit(api.onSubmit)(e);
              }}
            >
              <Stack bg="white" pt={40} pb={80} px={[20, 20, 80]} gap={40} borderRadius={5}>
                <RequiredFieldsNotice />
                <Box fontFamily="gilroyBold" fontSize={[28, 28, 36]}>
                  Your details
                  <Asterisk />
                </Box>
                <FormControl
                  as="fieldset"
                  labelProps={{
                    fontFamily: "gilroyBold",
                    fontSize: [28],
                    lineHeight: "130%"
                  }}
                  fields={
                    <>
                      <ControlledFormField
                        name="firstName"
                        label="First name"
                        Component={FloatingLabelInput}
                        editable={!readOnlyFields.includes("firstName")}
                      />
                      <ControlledFormField
                        name="lastName"
                        label="Last name"
                        Component={FloatingLabelInput}
                        editable={!readOnlyFields.includes("lastName")}
                      />
                      <ControlledFormField
                        name="email"
                        label="Email address"
                        Component={FloatingLabelInput}
                        editable={!readOnlyFields.includes("email")}
                      />
                      <ControlledFormField
                        name="phoneNumber"
                        label="Contact number"
                        Component={FloatingLabelInput}
                      />
                      <ControlledFormField
                        name="jobRole"
                        label="Job title / Role"
                        Component={FloatingLabelInput}
                      />
                      <ControlledFormField
                        name="businessName"
                        label="Organisation name"
                        Component={FloatingLabelInput}
                      />
                      <ControlledFormField
                        name="businessLocationCity"
                        label="Location City"
                        Component={FloatingLabelInput}
                      />
                      <ControlledFormField
                        name="businessCountry"
                        placeholder="Country"
                        Component={ShippingCountrySelect}
                        valueField="id"
                      />
                    </>
                  }
                />
                {showPasswordFields && (
                  <FormControl
                    as="fieldset"
                    label={
                      <Box fontFamily="gilroyBold" fontSize={[18]}>
                        Password
                        <Asterisk />
                      </Box>
                    }
                    fields={
                      <>
                        <ControlledFormField
                          name="password"
                          label="Password"
                          Component={FloatingLabelPasswordInput}
                        />
                        <ControlledFormField
                          name="confirmPassword"
                          label="Confirm Password"
                          Component={FloatingLabelPasswordInput}
                        />
                      </>
                    }
                  />
                )}
                {showLoginLink && (
                  <Box fontFamily="gilroyMedium" fontSize={16}>
                    Already a user? Click{" "}
                    <InternalTextLink href={CMP_SIGNIN_URL}>here</InternalTextLink> login
                  </Box>
                )}
                <ControlledFormField
                  name="dateOfBirth"
                  Component={DateInput}
                  getDay={dateStr => (dateStr ? dateStr.split("-")[2] : "")}
                  getMonth={dateStr => (dateStr ? dateStr.split("-")[1] : "")}
                  getYear={dateStr => (dateStr ? dateStr.split("-")[0] : "")}
                  constructDate={({ day, month, year }) => {
                    return `${year}-${month}-${day}`;
                  }}
                  label="Date of birth"
                  labelProps={{
                    fontFamily: "gilroyBold",
                    fontSize: 18,
                    mb: 20
                  }}
                  disabled={readOnlyFields.includes("dateOfBirth")}
                />
                <ControlledRadioGroup
                  Container={Stack}
                  containerProps={{
                    gap: 20
                  }}
                  name="gender"
                  label="Gender"
                  labelProps={{
                    fontFamily: "gilroyBold",
                    fontSize: 18,
                    mb: 20
                  }}
                  values={[
                    {
                      label: "Male",
                      value: "Male"
                    },
                    {
                      label: "Female",
                      value: "Female"
                    }
                  ]}
                  disabled={readOnlyFields.includes("gender")}
                />
              </Stack>
              <Stack bg="white" pt={40} pb={80} px={[20, 20, 80]} gap={40} borderRadius={5}>
                <FormControl
                  label={
                    <Box fontFamily="gilroyBold" fontSize={[18, 18, 28]} lineHeight="26px">
                      What is your profession?
                    </Box>
                  }
                  spaceAboveHelpText={2}
                  helpText={
                    <Box fontFamily="gilroyMedium" fontSize={16}>
                      Select your primary professional focus
                    </Box>
                  }
                  spaceAboveChildren={[20, 20, 40]}
                >
                  <Stack alignItems="flex-start" gap={[20, 20, 40]}>
                    <PractitionerTypeField practitionerTypes={practitionerTypes} />
                  </Stack>
                </FormControl>
                <QualificationProof practitionerTypes={practitionerTypes} />
              </Stack>
              <Stack bg="white" pt={40} pb={80} px={[20, 20, 80]} gap={40} borderRadius={5}>
                <FormControl
                  label={
                    <Box fontFamily="gilroyBold" fontSize={[18, 18, 28]} lineHeight="26px">
                      Professional Bodies
                    </Box>
                  }
                  spaceAboveHelpText={2}
                  helpText={
                    <Box fontFamily="gilroyMedium" fontSize={16}>
                      Please give details of any professional bodies you are registered with
                    </Box>
                  }
                  spaceAboveChildren={[20, 20, 40]}
                >
                  <ProfessionalBodiesField />
                </FormControl>
              </Stack>
              <Stack bg="white" pt={40} pb={80} px={[20, 20, 80]} gap={40} borderRadius={5}>
                <FormControl
                  label={
                    <Box fontFamily="gilroyBold" fontSize={[18, 18, 28]} lineHeight="26px">
                      Specialisms
                    </Box>
                  }
                  spaceAboveHelpText={2}
                  helpText={
                    <Box fontFamily="gilroyMedium" fontSize={16}>
                      Select all of the areas of expertise that you practice with your patients
                    </Box>
                  }
                  spaceAboveChildren={[20, 20, 40]}
                >
                  <Stack alignItems="flex-start" gap={[20, 20, 40]}>
                    <SpecialismsField specialisms={specialisms} />
                  </Stack>
                </FormControl>
              </Stack>
              <Stack bg="white" pt={40} pb={80} px={[20, 20, 80]} gap={40} borderRadius={5}>
                <FormControl
                  fields={
                    <>
                      <ControlledFormField
                        label={
                          <>
                            By submitting this form, I confirm that I have read and agree to the{" "}
                            <InternalTextLink href={REGENERUS_TERMS_OF_USE_URL}>
                              Terms of Service
                            </InternalTextLink>{" "}
                            and{" "}
                            <InternalTextLink href={REGENERUS_PRIVACY_URL}>
                              Privacy Policy
                            </InternalTextLink>{" "}
                            , and I consent to receive health-related news, exclusive promotions,
                            and product updates from Regenerus Lab by email.
                          </>
                        }
                        name="acceptTerms"
                        Component={Checkbox}
                        styles={{
                          labelProps: {
                            fontSize: 14,
                            lineHeight: "24px"
                          },
                          wrapperProps: {
                            alignItems: "flex-start"
                          },
                          checkbox: {
                            size: "lg"
                          }
                        }}
                      />
                    </>
                  }
                />
                <ButtonV2
                  color="dark"
                  rightIcon={<ChevronComponent />}
                  size={["sm", "sm", "md"]}
                  mx="auto"
                >
                  Submit
                </ButtonV2>
                {!!api.nonFieldError && <Error error={api.nonFieldError} />}
              </Stack>
            </Stack>
          </Box>
        </Box>
      </CustomerPage>
    </FormProvider>
  );
}
